<template>
  <div>
    <div class="large margin-bottom-xs" role="heading" aria-level="2">
      {{ GIFT_CARD_BALANCE.checkBalance }}
    </div>
    <div class="cls-notification-container" :class="{ 'margin-bottom-xxxl': lockout }">
      <Notification
        v-for="notification in notifications"
        :key="notification"
        :closable="false"
        :class="{
          'margin-bottom-xs': brand === 'mcom',
          'margin-bottom-s': brand === 'bcom',
        }"
      >
        <span v-text="notification.message"></span>
      </Notification>
    </div>
    <div v-if="balance" id="gift-card-balance" class="margin-bottom-xxs">
      Your current Gift Card balance: <br v-if="brand === 'bcom'" /><span class="heavy" v-text="balance"></span>
    </div>
    <div v-if="!lockout" class="grid-x padding-bottom-xxxl">
      <Form id="gift-card-lookup-form" v-slot="{ errors }" ref="formRef" class="cell medium-6 large-4" @submit="submit">
        <div id="gift-card-card-number">
          <div class="input-container">
            <Field
              id="gift-card-number"
              v-model="form.cardNumber"
              name="gift-card-number"
              type="text"
              :rules="giftCardNumber"
              placeholder=" "
              :maxlength="maxCardNumberLength"
              class="padding-end-icon"
              :class="{ 'is-invalid-input': !!errors['gift-card-number'] }"
              :aria-describedby="!!errors['gift-card-number'] ? 'gift-card-number-error' : undefined"
              @keypress="numbersOnly($event)"
              @paste.prevent="onPaste"
            />
            <label class="label-top" for="gift-card-number">Card number</label>
            <div class="input-inner-end">
              <button
                v-if="form.cardNumber"
                type="button"
                :class="{
                  'clear-filled': brand === 'mcom',
                  clear: brand === 'bcom',
                }"
                aria-label="Clear Card Number"
                @click="clearField('card')"
              ></button>
              <button
                v-else
                type="button"
                for="tooltip-card"
                :class="{
                  help: brand === 'mcom',
                  'tooltip-large': brand === 'bcom',
                }"
                aria-label="Help for Card Number"
                @click="toggleTooltip($event, 'cardNumber')"
              ></button>
            </div>
            <Toggletip ref="cardNumberToolTipRef">
              <template v-if="brand === 'mcom'">
                <div class="white-color heavy">{{ GIFT_CARD_BALANCE.egiftCardLabel }}</div>
                <div>{{ GIFT_CARD_BALANCE.cardText }}</div>
                <div class="white-color heavy margin-top-s">{{ GIFT_CARD_BALANCE.giftCardLabel }}</div>
                <div>
                  <img class="card-help-img" :src="giftCardNumberImgMcom" :alt="GIFT_CARD_BALANCE.imageAltText" />
                </div>
              </template>

              <template v-if="brand === 'bcom'">
                <div class="margin-bottom-s" v-html="GIFT_CARD_BALANCE.usingGiftCard"></div>
                <div class="margin-top-xs">
                  <img :src="giftCardImgBcom" :alt="GIFT_CARD_BALANCE.imageAltText" />
                </div>
              </template>
            </Toggletip>
          </div>
          <ErrorMessage v-slot="{ message }" name="gift-card-number">
            <div id="gift-card-number-error" role="alert" class="form-error is-visible">
              <span class="error-red" aria-hidden="true"></span>
              <span class="form-error-text">{{ message }}</span>
            </div>
          </ErrorMessage>
        </div>
        <div v-if="brand === 'mcom'" id="gift-card-cid-number-div" class="margin-top-s">
          <div class="input-container">
            <Field
              id="gift-card-cid-number"
              v-model="form.cid"
              name="gift-card-cid-number"
              type="text"
              :rules="giftCardCidNumber"
              placeholder=" "
              maxlength="4"
              class="padding-end-icon"
              :class="{ 'is-invalid-input': !!errors['gift-card-cid-number'] }"
              :aria-describedby="!!errors['gift-card-cid-number'] ? 'gift-card-cid-number-error' : undefined"
              @keypress="numbersOnly($event)"
            />
            <label class="label-top" for="gift-card-cid-number" aria-label="CID Number">CID number</label>
            <div class="input-inner-end">
              <button
                v-if="form.cid"
                type="button"
                :class="{
                  'clear-filled': brand === 'mcom',
                  clear: brand === 'bcom',
                }"
                aria-label="Clear CID Number"
                @click="clearField('cid')"
              ></button>
              <button
                v-else
                type="button"
                :class="{
                  help: brand === 'mcom',
                  'tooltip-large': brand === 'bcom',
                }"
                aria-label="Help for Cid Number"
                for="tooltip-cid"
                @click="toggleTooltip($event, 'cidNumber')"
              ></button>
            </div>
            <Toggletip ref="cidToolTipRef">
              <div class="white-color heavy">{{ GIFT_CARD_BALANCE.egiftCardLabel }}</div>
              <div>{{ GIFT_CARD_BALANCE.cidText }}</div>
              <div class="white-color heavy margin-top-s">{{ GIFT_CARD_BALANCE.giftCardLabel }}</div>
              <div>
                <img class="card-help-img" :src="giftCardCidImgMcom" :alt="GIFT_CARD_BALANCE.cidImageAltText" />
              </div>
            </Toggletip>
          </div>
          <ErrorMessage v-slot="{ message }" name="gift-card-cid-number">
            <div id="gift-card-cid-number-error" role="alert" class="form-error is-visible">
              <span class="error-red" aria-hidden="true"></span>
              <span class="form-error-text">{{ message }}</span>
            </div>
          </ErrorMessage>
        </div>
        <div id="gift-card-recaptcha" class="margin-top-s">
          <GoogleRecaptcha :reset="resetCaptcha" />
        </div>
        <div class="margin-top-s">
          <button id="form-submit" :disabled="disabled" type="submit" class="button expanded primary" @click="ctaClicked">
            <Spinner v-if="loadingBalance" />
            <span v-else>View Balance</span>
          </button>
        </div>
      </Form>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { storeToRefs } from 'pinia';
import { veevalidate } from '@vue-util/forms';
import Notification from '@atomic-ui/notification/Notification';
import { fireTag } from '@vue-util/tagManager';
import { Cookie } from '@vue-util/cookie';
import Spinner from '@atomic-ui/spinner/Spinner';
import Toggletip from '@atomic-ui/toggletip/Toggletip';
import isChallengeActive from '@vue-util/fraudPreventionChallenge';
import GoogleRecaptcha from '@/components/common/GoogleRecaptcha';
import { brand, GIFT_CARD_BALANCE } from 'config';
import { useGiftCardBalanceStore } from '@/stores/useGiftCardBalanceStore';
import giftCardNumberImgMcom from '@/assets/img/gift-card-number.jpg';
import giftCardCidImgMcom from '@/assets/img/gift-card-cid.jpg';
import giftCardImgBcom from '@/assets/img/bcom-gift-card.png';
import { useEnvPropsStore } from '@/stores/useEnvPropsStore';
import * as logger from '@vue-util/universalLogger/index.js';

const { Field, Form, ErrorMessage } = veevalidate;

const giftCardBalanceStore = useGiftCardBalanceStore();
const { form: formState, balance, lockout, loadingBalance, notifications } = storeToRefs(giftCardBalanceStore);
const { saveGiftCardForm, setLockoutMode, setGiftCardBalance } = giftCardBalanceStore;

const envPropsStore = useEnvPropsStore();
const { googleEnterpriseRecaptchaSiteKey } = storeToRefs(envPropsStore);
const formRef = ref(null);
const cardNumberToolTipRef = ref(null);
const cidToolTipRef = ref(null);

const form = ref({
  cardNumber: '',
  cardNumberMasked: '',
  cardNumberEncrypted: '',
  cid: '',
  googleRecaptchaResponse: '',
});
const resetCaptcha = ref(false);

watch(
  formState,
  (newVal) => {
    form.value = { ...newVal, googleRecaptchaResponse: form.value.googleRecaptchaResponse };
  },
  { deep: true },
);

onMounted(async () => {
  form.value = { ...formState.value, googleRecaptchaResponse: '' };
  await setLockoutMode(isLockoutFromCookie());
});

const disabled = computed(() => {
  if (loadingBalance.value) return true;
  if (brand === 'bcom') return form.value.cardNumber.length < 18;
  if (brand === 'mcom') {
    return form.value.cardNumber.length < 10 || form.value.cid.length < 3;
  }
  return true;
});

const maxCardNumberLength = computed(() => {
  if (brand === 'mcom') return 17;
  if (brand === 'bcom') return 19;
  return 0;
});

const toggleTooltip = (event, refValue) => {
  if (refValue === 'cardNumber') {
    cardNumberToolTipRef.value.toggle(event);
  } else {
    cidToolTipRef.value.toggle(event);
  }
};

async function submit() {
  if (disabled.value) return;
  let captchaChallengeListener = null;
  try {
    captchaChallengeListener = setInterval(() => {
      if (isChallengeActive()) {
        fireTag('link', {
          link_name: 'google recaptcha challenge',
          event_name: 'google recaptcha challenge',
        });
        clearInterval(captchaChallengeListener);
      }
    }, 100);

    if (window.grecaptcha?.enterprise) {
      await new Promise((resolve, reject) => {
        const timeout = setTimeout(() => reject(new Error('reCAPTCHA ready() timed out')), 5000);
        window.grecaptcha.enterprise.ready(() => {
          clearTimeout(timeout);
          resolve();
        });
      });
      form.value.googleRecaptchaResponse = await window.grecaptcha.enterprise.execute(googleEnterpriseRecaptchaSiteKey.value, {
        action: 'gift_card_balance',
      });
    }
  } catch (error) {
    logger.error('Google reCAPTCHA Enterprise script failed', error);
    return;
  } finally {
    if (captchaChallengeListener) {
      clearInterval(captchaChallengeListener);
    }
  }

  const response = await saveGiftCardForm(form.value);
  if (response.success) {
    if (brand === 'mcom') {
      fireTag('link', {
        event_name: 'gift card view balance',
        link_name: `gc balance remaining ${response.balance}`,
        page_type: 'my account',
      });
    }
    if (brand === 'bcom') {
      fireTag('link', {
        event_name: `gc balance remaining ${response.balance}`,
        link_name: 'gift card view balance',
        page_type: 'my account',
      });
    }
  }
  if (response.errors) {
    const errorCodes = [];
    const errorMsgs = [];
    response.errors.forEach((e) => {
      errorCodes.push(e.errorCode);
      errorMsgs.push(e.message);
    });

    const recaptchaError = errorCodes.includes('30004');

    if (brand === 'mcom') {
      fireTag('link', {
        event_name: recaptchaError ? 'google recaptcha error' : 'gift card view balance error link',
        error_category: 'my account',
        error_code: errorCodes,
        error_label: ['giftCard balance error'],
        error_scope: ['global'],
        error_message: errorMsgs,
        error_misc: [''],
      });
    }
    if (brand === 'bcom') {
      fireTag('link', {
        event_name: recaptchaError ? 'google recaptcha error' : 'gift card view balance error link',
        link_name: 'gift card view balance error link',
        error_category: 'my account',
        error_code: errorCodes,
        error_label: ['giftCard balance error'],
        error_scope: ['global'],
        error_message: errorMsgs,
        error_misc: [''],
      });
    }
  }
  resetCaptcha.value = !resetCaptcha.value;
  form.value.googleRecaptchaResponse = '';
}

function numbersOnly(e) {
  const char = String.fromCharCode(e.keyCode);
  if (/^[0-9]+$/.test(char)) return true;
  if (e.metaKey && (char === 'v' || char === 'V')) {
    return true;
  }
  return e.preventDefault();
}

function giftCardNumber(value) {
  if (brand === 'mcom' && (!value || !/^([0-9]{10,17})$|^\*{6,13}[0-9]{4}$/.test(value))) {
    return 'Your card number is the first set of digits beneath the bar code';
  }
  if (brand === 'bcom' && (!value || !/^([0-9]{18,19})$|^\*{14,15}[0-9]{4}$/.test(value))) {
    return 'Your card number is the first set of digits beneath the bar code';
  }
  return true;
}

function giftCardCidNumber(value) {
  if (!value || !/^[0-9]{3,4}$/.test(value)) {
    return 'Your CID number is the second set of 3-4 digits beneath the bar code';
  }
  return true;
}

function isLockoutFromCookie() {
  let cookiesArray = [];
  const gcaCookie = Cookie.get('SNSGCs');
  let attempt = 1;
  if (gcaCookie) {
    cookiesArray = gcaCookie.split('3_87_');
    cookiesArray.forEach((cookie) => {
      if (cookie.includes('gca')) {
        [, attempt] = cookie.split('1_92_');
      }
    });
  }
  return (parseInt(attempt, 10) || 1) === 3;
}

function ctaClicked() {
  fireTag('link', {
    event_name: 'gift card view balance',
    link_name: 'gift card view balance',
    page_type: 'my account',
  });
}

function clearField(field = '') {
  if (field === 'card') {
    form.value.cardNumber = '';
    form.value.cid = '';
    formRef.value.resetForm();
  }
  if (field === 'cid') {
    form.value.cid = '';
  }
  if (!balance.value) return;

  // reset balance and fire analytic tag
  setGiftCardBalance('');
  if (brand === 'mcom') {
    fireTag('link', {
      event_name: 'gift card view balance',
      link_name: 'new gc check initiated',
      page_type: 'my account',
    });
  }
  if (brand === 'bcom') {
    fireTag('link', {
      event_name: 'new gc check initiated',
      link_name: 'gift card view balance',
      page_type: 'my account',
    });
  }
}

function onPaste(event) {
  const clipboardData = event.clipboardData || window.clipboardData;
  if (clipboardData) {
    const clipboardText = clipboardData.getData('Text');
    if (clipboardText) {
      const spacesRemovedData = clipboardText.replace(/\s/g, '');
      form.value.cardNumber = spacesRemovedData;
    }
  }
  return true;
}
</script>

<style lang="scss" scoped>
.card-help-img {
  border-radius: rem-calc(12px);
}
.input-inner-end > button {
  cursor: pointer;
}
@include breakpoint(medium down) {
  #gift-card-lookup-form {
    position: relative;
  }
}

.cls-notification-container {
  min-height: 0;
  contain: layout style;
  content-visibility: auto;
  contain-intrinsic-size: auto 0;
}
</style>

<style lang="scss" brand="mcom" scoped>
@import '~@core/csg/src/scss/encodedIcons/mcom/_help.scss';
@import '~@core/csg/src/scss/encodedIcons/mcom/_clear-filled.scss';
@import '~@core/csg/src/scss/encodedIcons/mcom/error-red.scss';
</style>

<style lang="scss" brand="bcom" scoped>
@import '~@core/csg/src/scss/encodedIcons/bcom/_tooltip-large.scss';
@import '~@core/csg/src/scss/encodedIcons/bcom/_clear.scss';
@import '~@core/csg/src/scss/encodedIcons/mcom/error-red.scss';
</style>
